import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser, useClerk } from '@clerk/react';
import axios from 'axios';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import {
    Users, MessageSquare, Star, ShieldCheck, LayoutGrid, LogOut, Menu, X,
    RefreshCw, ChevronDown, ChevronUp, ShieldAlert, ShieldHalf
} from 'lucide-react';
import { API_BASE_URL } from '../config';
import './AdminDashboard.css';

const NAV = [
    { id: 'overview', label: 'Overview', Icon: LayoutGrid, sub: 'Live snapshot' },
    { id: 'users', label: 'Users', Icon: Users, sub: 'Registered accounts' },
    { id: 'queries', label: 'Queries', Icon: MessageSquare, sub: 'Fact-check logs' },
    { id: 'feedback', label: 'Feedback', Icon: Star, sub: 'Survey responses' },
    { id: 'judge', label: 'Judge', Icon: ShieldCheck, sub: 'GPT-4o audits' },
];

// score -> semantic status class (reserved status colors, always with a label)
const scoreStatus = (s) => (s >= 71 ? 'good' : s >= 31 ? 'warn' : 'crit');
const judgeStatus = (s) => (s >= 8 ? 'good' : s >= 5 ? 'warn' : 'crit');

const AdminDashboard = () => {
    const navigate = useNavigate();
    const { isLoaded, isSignedIn, getToken } = useAuth();
    const { user } = useUser();
    const { signOut } = useClerk();

    const [activeTab, setActiveTab] = useState('overview');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [expandedJudgeId, setExpandedJudgeId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [authz, setAuthz] = useState('checking'); // 'checking' | 'ok' | 'denied'
    const [page, setPage] = useState(1);
    const [userOptions, setUserOptions] = useState([]);
    const [queryUser, setQueryUser] = useState('');   // '' = all users
    const [querySort, setQuerySort] = useState('recent'); // 'recent' | 'user'
    const [data, setData] = useState({
        stats: {}, distribution: {}, plan_distribution: {},
        trends: {}, series: [], recent: [],
        users: { items: [], total: 0 }, queries: { items: [], total: 0 },
        feedback: { items: [], total: 0 }, judge: { items: [], total: 0 },
    });

    const adminName = user?.firstName || user?.fullName
        || user?.primaryEmailAddress?.emailAddress?.split('@')[0] || 'Admin';

    // Not signed in -> Clerk login.
    useEffect(() => {
        if (isLoaded && !isSignedIn) navigate('/login');
    }, [isLoaded, isSignedIn, navigate]);

    const fetchData = useCallback(async () => {
        if (!isSignedIn) return;
        setLoading(true);
        setError(null);
        try {
            const token = await getToken();
            const config = { headers: { Authorization: `Bearer ${token}` } };

            if (activeTab === 'overview') {
                const res = await axios.get(`${API_BASE_URL}/admin/overview`, config);
                setData((p) => ({
                    ...p,
                    stats: res.data.stats,
                    distribution: res.data.distribution,
                    plan_distribution: res.data.plan_distribution || {},
                    trends: res.data.trends || {},
                    series: res.data.queries_over_time || [],
                    recent: res.data.recent_queries || [],
                }));
            } else if (activeTab === 'users') {
                const res = await axios.get(`${API_BASE_URL}/admin/users?page=${page}&limit=10`, config);
                setData((p) => ({ ...p, users: res.data }));
            } else if (activeTab === 'queries') {
                const params = new URLSearchParams({ page: String(page), limit: '10' });
                if (queryUser) params.set('user_id', queryUser);
                if (querySort !== 'recent') params.set('sort', querySort);
                const res = await axios.get(`${API_BASE_URL}/admin/queries?${params.toString()}`, config);
                setData((p) => ({ ...p, queries: res.data }));
            } else if (activeTab === 'feedback') {
                const res = await axios.get(`${API_BASE_URL}/admin/feedback?page=${page}&limit=10`, config);
                setData((p) => ({ ...p, feedback: res.data }));
            } else if (activeTab === 'judge') {
                const res = await axios.get(`${API_BASE_URL}/admin/queries?page=${page}&limit=10`, config);
                setData((p) => ({ ...p, judge: res.data }));
            }
            setAuthz('ok');
        } catch (err) {
            if (err.response?.status === 403) {
                setAuthz('denied');
            } else if (err.response?.status === 401) {
                navigate('/login');
            } else {
                setError(`Connection error: ${err.response?.data?.detail || err.message}`);
            }
        } finally {
            setLoading(false);
        }
    }, [activeTab, page, queryUser, querySort, getToken, isSignedIn, navigate]);

    useEffect(() => {
        if (isLoaded && isSignedIn) fetchData();
    }, [isLoaded, isSignedIn, fetchData]);

    // Load the {id, name} user list once (for the Queries filter dropdown).
    const fetchUserOptions = useCallback(async () => {
        if (!isSignedIn) return;
        try {
            const token = await getToken();
            const res = await axios.get(`${API_BASE_URL}/admin/users/options`, { headers: { Authorization: `Bearer ${token}` } });
            setUserOptions(res.data.items || []);
        } catch { /* non-fatal; dropdown just stays empty */ }
    }, [getToken, isSignedIn]);

    useEffect(() => {
        if (isLoaded && isSignedIn && authz === 'ok') fetchUserOptions();
    }, [isLoaded, isSignedIn, authz, fetchUserOptions]);

    const filterToUser = (id) => { setQueryUser(String(id)); setPage(1); setActiveTab('queries'); };
    const goTab = (id) => { setActiveTab(id); setPage(1); setIsSidebarOpen(false); };
    const handleSignOut = async () => { await signOut(); navigate('/login'); };

    // ---- gate states ----
    if (!isLoaded || (isLoaded && !isSignedIn)) {
        return <div className="adm-boot"><div className="adm-spinner" /><p>Checking access…</p></div>;
    }
    if (authz === 'denied') {
        return (
            <div className="adm-boot">
                <div className="adm-denied">
                    <ShieldAlert size={52} />
                    <h2>Admin access required</h2>
                    <p>Your account ({adminName}) isn’t an administrator. Ask an existing admin to grant you access.</p>
                    <div className="adm-denied-actions">
                        <button className="adm-btn" onClick={() => navigate('/app')}>Back to app</button>
                        <button className="adm-btn ghost" onClick={handleSignOut}>Sign out</button>
                    </div>
                </div>
            </div>
        );
    }

    const meta = NAV.find((n) => n.id === activeTab);
    const CAT = { link: 'var(--adm-cat2)', text: 'var(--adm-cat1)', image: 'var(--adm-cat3)' };
    const distEntries = Object.entries(data.distribution || {});
    const distTotal = distEntries.reduce((s, [, v]) => s + v, 0);
    const planEntries = Object.entries(data.plan_distribution || {});
    const planTotal = planEntries.reduce((s, [, v]) => s + v, 0);
    const planColor = (name) => name.includes('premium') ? 'var(--adm-cat1)' : name.includes('basic') ? 'var(--adm-cat2)' : 'var(--adm-muted)';
    const planLabel = (name) => name.replace('_subscription', '');
    const paidPct = planTotal ? Math.round(((planTotal - (data.plan_distribution?.free || 0)) / planTotal) * 100) : 0;
    const judgePct = Math.round(((data.stats.avg_judge_score || 0) / 10) * 100);

    const pageInfo = data[activeTab] || { items: [], total: 0 };

    return (
        <div className="admin-shell">
            {/* Sidebar */}
            <aside className={`adm-side ${isSidebarOpen ? 'open' : ''}`}>
                <div className="adm-brand">
                    <img src="/images/just_logo_bgfree.jpg" alt="" />
                    <div><b>VerDict</b><div className="adm-tag">Admin Console</div></div>
                    <button className="adm-mobile adm-x" onClick={() => setIsSidebarOpen(false)}><X size={20} /></button>
                </div>
                <nav className="adm-nav">
                    {NAV.map(({ id, label, Icon }) => (
                        <button key={id} className={activeTab === id ? 'on' : ''} onClick={() => goTab(id)}>
                            <Icon size={17} /> {label}
                        </button>
                    ))}
                </nav>
                <div className="adm-foot">
                    <div className="adm-who">
                        <div className="adm-av">{adminName.charAt(0).toUpperCase()}</div>
                        <div><div className="adm-n">{adminName}</div><div className="adm-r">Administrator</div></div>
                    </div>
                    <button className="adm-logout" onClick={handleSignOut}><LogOut size={15} /> Sign out</button>
                </div>
            </aside>
            {isSidebarOpen && <div className="adm-scrim" onClick={() => setIsSidebarOpen(false)} />}

            {/* Main */}
            <main className="adm-main">
                <header className="adm-mobilebar adm-mobile">
                    <button onClick={() => setIsSidebarOpen(true)}><Menu size={22} /></button>
                    <span>VerDict Admin</span><span style={{ width: 22 }} />
                </header>

                <div className="adm-top">
                    <div>
                        <h1>{meta.label}</h1>
                        <div className="adm-sub">{meta.sub}{pageInfo.total ? ` · ${pageInfo.total.toLocaleString()} total` : ''}</div>
                    </div>
                    <button className="adm-btn" onClick={fetchData} disabled={loading}>
                        <RefreshCw size={14} /> {loading ? 'Loading…' : 'Refresh'}
                    </button>
                </div>

                {error && <div className="adm-error">{error} <button onClick={fetchData}>Retry</button></div>}

                {/* OVERVIEW */}
                {activeTab === 'overview' && (
                    <>
                        <div className="adm-tiles">
                            <Tile icon={<Users size={14} />} tint="cat1" label="Total Users" value={data.stats.total_users ?? 0} delta={data.trends?.total_users} />
                            <Tile icon={<MessageSquare size={14} />} tint="cat2" label="Total Queries" value={data.stats.total_queries ?? 0} delta={data.trends?.total_queries} spark={data.series?.map((s) => s.count)} />
                            <Tile icon={<ShieldHalf size={14} />} tint="good" label="Avg Truth Score" value={`${data.stats.avg_truth_score ?? 0}%`} delta={data.trends?.avg_truth_score} />
                            <Tile icon={<Star size={14} />} tint="warn" label="Avg NPS" value={data.stats.avg_nps ?? 0} delta={data.trends?.avg_nps} />
                            <Tile icon={<ShieldCheck size={14} />} tint="accent" label="Paid Users" value={data.stats.paid_users ?? 0} />
                        </div>

                        <div className="adm-gridL">
                            <div className="adm-panel">
                                <h3>Queries over time</h3><div className="adm-psub">Last 14 days · daily volume</div>
                                <LineChart series={data.series} />
                            </div>
                            <Donut title="Query types" sub="Share of all checks"
                                center={distTotal >= 1000 ? `${(distTotal / 1000).toFixed(1)}k` : distTotal} centerSub="total"
                                entries={distEntries} total={distTotal}
                                colorFor={(n) => CAT[n] || 'var(--adm-muted)'} labelFor={(n) => n} />
                            <Donut title="Subscription plans" sub="Active user base"
                                center={`${paidPct}%`} centerSub="paid"
                                entries={planEntries} total={planTotal}
                                colorFor={planColor} labelFor={planLabel} />
                        </div>

                        <div className="adm-grid2">
                            <div className="adm-panel">
                                <h3>Judge quality audit</h3><div className="adm-psub">GPT-4o audit of model verdicts</div>
                                <div className="adm-meter">
                                    <div className="adm-meter-row">
                                        <span><span className="adm-big">{data.stats.avg_judge_score ?? 0}</span><span className="adm-of"> / 10</span></span>
                                        <span className="adm-status good">meeting {judgePct}%</span>
                                    </div>
                                    <div className="adm-track"><div className="adm-fill" style={{ width: `${judgePct}%` }} /></div>
                                </div>
                                <div className="adm-substats">
                                    <div><span>Feedback</span><b>{data.stats.total_feedback ?? 0}</b></div>
                                    <div><span>Truth avg</span><b>{data.stats.avg_truth_score ?? 0}%</b></div>
                                </div>
                            </div>
                            <div className="adm-panel">
                                <h3>Recent activity</h3><div className="adm-psub">Latest checks across all users</div>
                                <div className="adm-recent">
                                    {(data.recent || []).map((r) => (
                                        <div className="adm-recent-row" key={r.id}>
                                            <span className={`adm-t t-${r.type}`}>{r.type}</span>
                                            <span className="adm-recent-txt">{r.verdict || 'Pending'}</span>
                                            <span className="adm-recent-user">{r.user_name}</span>
                                            <span className={`adm-status ${scoreStatus(r.score || 0)}`}>{r.score ?? '—'}</span>
                                        </div>
                                    ))}
                                    {(!data.recent || data.recent.length === 0) && <div className="adm-empty">No activity yet</div>}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* Queries: filter by user + sort */}
                {activeTab === 'queries' && (
                    <div className="adm-qbar">
                        <select
                            className="adm-select"
                            value={queryUser}
                            onChange={(e) => { setQueryUser(e.target.value); setPage(1); }}
                        >
                            <option value="">All users</option>
                            {userOptions.map((u) => <option key={u.id} value={u.id}>{u.name} (#{u.id})</option>)}
                        </select>
                        <div className="adm-seg2">
                            <button className={querySort === 'recent' ? 'on' : ''} onClick={() => { setQuerySort('recent'); setPage(1); }}>Recent</button>
                            <button className={querySort === 'user' ? 'on' : ''} onClick={() => { setQuerySort('user'); setPage(1); }}>By user</button>
                        </div>
                        {queryUser && (
                            <span className="adm-fchip">
                                {userOptions.find((u) => String(u.id) === queryUser)?.name || `#${queryUser}`}
                                <button onClick={() => { setQueryUser(''); setPage(1); }} title="Clear filter" aria-label="Clear filter">✕</button>
                            </span>
                        )}
                    </div>
                )}

                {/* Pagination for list tabs */}
                {activeTab !== 'overview' && pageInfo.total > 0 && (
                    <div className="adm-pager">
                        <span>Showing {(page - 1) * 10 + 1}–{Math.min(page * 10, pageInfo.total)} of {pageInfo.total}</span>
                        <div>
                            <button className="adm-btn ghost" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
                            <span className="adm-pagenum">Page {page}</span>
                            <button className="adm-btn ghost" disabled={page * 10 >= pageInfo.total} onClick={() => setPage((p) => p + 1)}>Next</button>
                        </div>
                    </div>
                )}

                {/* USERS */}
                {activeTab === 'users' && (
                    <div className="adm-tablewrap">
                        <table className="adm-table">
                            <thead><tr><th>Name</th><th>Identifier</th><th>Platform</th><th>Plan</th><th>Queries</th><th>Last active</th><th>Joined</th></tr></thead>
                            <tbody>
                                {data.users.items.map((u) => (
                                    <tr key={u.id}>
                                        <td>{u.name}</td>
                                        <td className="dim">{u.is_whatsapp ? u.whatsapp : u.email}</td>
                                        <td>{u.is_whatsapp ? '🟢 WhatsApp' : '🌐 Web'}</td>
                                        <td><span className={`adm-pill p-${u.plan ? planLabel(u.plan) : 'free'}`}>{u.plan ? planLabel(u.plan) : 'free'}</span></td>
                                        <td className="num">{u.query_count}</td>
                                        <td className="dim">{u.last_active_at ? new Date(u.last_active_at).toLocaleDateString() : '—'}</td>
                                        <td className="dim">{new Date(u.created_at).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* QUERIES */}
                {activeTab === 'queries' && (
                    <div className="adm-stack">
                        {!loading && data.queries.items.length === 0 && (
                            <div className="adm-empty">No queries{queryUser ? ' for this user' : ''} yet.</div>
                        )}
                        {data.queries.items.map((q) => (
                            <div key={q.id} className="adm-log">
                                <div className="adm-log-top">
                                    <span className={`adm-t t-${q.type}`}>{q.type}</span>
                                    <button type="button" className="adm-log-user adm-userbtn" onClick={() => filterToUser(q.user_id)} title="Filter to this user">
                                        {q.user_name} <span className="id">#{q.user_id}</span>
                                    </button>
                                    {q.hallucination_detected && <span className="adm-halluc">⚠ Hallucination</span>}
                                    <span className="adm-log-time">{new Date(q.timestamp).toLocaleString()}</span>
                                </div>
                                {q.type === 'image' && (q.media_id || (q.text && q.text.startsWith('http'))) && (
                                    <div className="adm-media"><img src={q.media_id ? `${API_BASE_URL}/media/${q.media_id}` : q.text} alt="" onError={(e) => (e.target.style.display = 'none')} /></div>
                                )}
                                <p className="adm-log-text">{q.text}</p>
                                {q.verdict && (
                                    <div className="adm-log-res">
                                        <span className={`adm-status ${scoreStatus(q.score)}`}>{q.verdict}</span>
                                        <span className="num">Score {q.score}/100</span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* FEEDBACK */}
                {activeTab === 'feedback' && (
                    <div className="adm-cards">
                        {data.feedback.items.map((f) => (
                            <div key={f.id} className="adm-log">
                                <div className="adm-log-user" style={{ marginBottom: 10 }}>{f.user_name} <span className="id">#{f.user_id}</span></div>
                                <div className="adm-fscores">
                                    <div><span>Accuracy</span><b>★ {f.accuracy}</b></div>
                                    <div><span>Ease</span><b>★ {f.ease}</b></div>
                                    <div><span>NPS</span><b>{f.nps}</b></div>
                                </div>
                                {f.comments && <p className="adm-log-text" style={{ fontStyle: 'italic' }}>“{f.comments}”</p>}
                                {f.requests && <p className="adm-log-text"><b>Requests:</b> {f.requests}</p>}
                                <div className="adm-fdate">{new Date(f.submitted_at).toLocaleDateString()}</div>
                            </div>
                        ))}
                    </div>
                )}

                {/* JUDGE */}
                {activeTab === 'judge' && (
                    <div className="adm-stack">
                        {data.judge.items.map((q) => (
                            <div key={q.id} className="adm-log">
                                <div className="adm-log-top">
                                    <span className="adm-log-user">{q.user_name} <span className="id">#{q.user_id}</span></span>
                                    <span className={`adm-judge-badge ${judgeStatus(q.judge_score || 0)}`} style={{ marginLeft: 'auto' }}>{q.judge_score ?? '?'} / 10</span>
                                </div>
                                <div className="adm-flabel">Query</div>
                                <p className="adm-log-text">{q.text}</p>
                                <div className="adm-log-res" style={{ marginBottom: q.hallucination_detected ? 12 : 0 }}>
                                    <span className={`adm-status ${scoreStatus(q.score)}`}>{q.verdict} · {q.score}/100</span>
                                    {q.hallucination_detected && <span className="adm-halluc">⚠ Hallucination</span>}
                                </div>
                                {q.hallucination_detected && q.hallucination_notes && (
                                    <div className="adm-halluc-box">
                                        <div className="hh">Factual hallucination detected</div>
                                        <p>{q.hallucination_notes}</p>
                                    </div>
                                )}
                                <button className="adm-expand" onClick={() => setExpandedJudgeId(expandedJudgeId === q.id ? null : q.id)}>
                                    Judge’s analysis {expandedJudgeId === q.id ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                                </button>
                                {expandedJudgeId === q.id && (
                                    <p className="adm-log-text" style={{ marginTop: 8 }}>{q.judge_analysis || 'No judge analysis available.'}</p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

// ---- small presentational pieces ----
const Delta = ({ d }) => {
    if (!d || d.value === null || d.value === undefined) return null;
    const v = d.value;
    const dir = v > 0 ? 'up' : v < 0 ? 'down' : 'flat';
    const arrow = v > 0 ? '▲' : v < 0 ? '▼' : '—';
    const text = v === 0 ? 'steady' : `${Math.abs(v)}${d.unit === '%' ? '%' : ' pts'}`;
    return <span className={`adm-delta ${dir}`}>{arrow} {text} <span className="adm-delta-note">vs prev 7d</span></span>;
};

const Sparkline = ({ data }) => {
    if (!data || data.length < 2) return null;
    const w = 120, h = 26, max = Math.max(...data, 1), min = Math.min(...data, 0);
    const span = (max - min) || 1;
    const pts = data.map((v, i) => `${(i * w) / (data.length - 1)},${h - 2 - ((v - min) / span) * (h - 4)}`).join(' ');
    return (
        <svg className="adm-spark" width="100%" height="26" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
            <polyline fill="none" stroke="var(--adm-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={pts} />
        </svg>
    );
};

const LineChart = ({ series }) => {
    const data = (series || []).map((s) => s.count);
    if (data.length === 0) return <div className="adm-empty">No query data yet</div>;
    const w = 360, h = 150, pad = { l: 6, r: 6, t: 12, b: 8 };
    const max = Math.max(...data, 4), min = 0, span = (max - min) || 1;
    const x = (i) => pad.l + (i * (w - pad.l - pad.r)) / ((data.length - 1) || 1);
    const y = (v) => pad.t + (1 - (v - min) / span) * (h - pad.t - pad.b);
    const pts = data.map((v, i) => `${x(i)},${y(v)}`).join(' ');
    const area = `${pad.l},${h - pad.b} ${pts} ${w - pad.r},${h - pad.b}`;
    const ticks = [...new Set([0, Math.round(max / 2), max])];
    const li = data.length - 1;
    return (
        <svg width="100%" height="150" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
            {ticks.map((v) => (
                <g key={v}>
                    <line className="adm-gridline" x1={pad.l} y1={y(v)} x2={w - pad.r} y2={y(v)} />
                    <text className="adm-tick" x={w - pad.r} y={y(v) - 2} textAnchor="end">{v}</text>
                </g>
            ))}
            <polygon className="adm-area" points={area} />
            <polyline className="adm-lchart" points={pts} />
            <circle className="adm-endpt" cx={x(li)} cy={y(data[li])} r="3.5" />
        </svg>
    );
};

const Tile = ({ icon, tint, label, value, delta, spark }) => (
    <div className="adm-tile">
        <div className="adm-tile-lab"><span className={`adm-ic ${tint}`}>{icon}</span>{label}</div>
        <div className="adm-tile-val">{typeof value === 'number' ? value.toLocaleString() : value}</div>
        <Delta d={delta} />
        {spark && <Sparkline data={spark} />}
    </div>
);

const Donut = ({ title, sub, center, centerSub, entries, total, colorFor, labelFor }) => {
    const chartData = entries.map(([name, value]) => ({ name, value }));
    return (
        <div className="adm-panel">
            <h3>{title}</h3><div className="adm-psub">{sub}</div>
            <div className="adm-donut-wrap">
                <div className="adm-donut">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                            <Pie data={chartData} dataKey="value" cx="50%" cy="50%" innerRadius="66%" outerRadius="100%" paddingAngle={3} stroke="none">
                                {chartData.map((e) => <Cell key={e.name} fill={colorFor(e.name)} />)}
                            </Pie>
                            <Tooltip
                                formatter={(v, n) => [`${v} (${total ? Math.round((v / total) * 100) : 0}%)`, labelFor(n)]}
                                wrapperStyle={{ zIndex: 10 }}
                                allowEscapeViewBox={{ x: true, y: true }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="adm-donut-ctr"><b>{center}</b><span>{centerSub}</span></div>
                </div>
                <div className="adm-legend">
                    {entries.map(([name, value]) => (
                        <div className="row" key={name}>
                            <span className="sw" style={{ background: colorFor(name) }} />
                            <span className="nm">{labelFor(name)}</span>
                            <span className="pc">{total ? Math.round((value / total) * 100) : 0}%</span>
                        </div>
                    ))}
                    {entries.length === 0 && <span className="nm">No data yet</span>}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
